import axios from 'axios'

import { apiClient } from '@/api/client'
import { parseUtcDate } from '@/lib/date-utils'
import {
  deleteOfflineHighlight,
  findOfflineHighlightByServerId,
  getOfflineHighlight,
  getOfflineHighlightsByEpisode,
  type OfflineHighlight,
  replaceCachedHighlights,
  saveOfflineHighlight,
} from '@/lib/db'

// ============================================================
// 型定義
// ============================================================

export type MediaType = 'image' | 'epub' | 'pdf' | 'video' | 'audio'

export type HighlightSelectionRect = {
  x: number
  y: number
  width: number
  height: number
}

export type HighlightSelection = {
  page?: number
  rect?: HighlightSelectionRect
}

export type HighlightPositionData = {
  selections?: HighlightSelection[]
  cfi?: string // EPUB用のCFI位置 (deprecated: Readium Locator に移行)
  /** Readium Locator JSON (新形式) */
  locator?: Record<string, unknown>
  // PDF用のハイライトエリア（@react-pdf-viewer/highlightのHighlightArea型と互換）
  highlightAreas?: {
    height: number
    left: number
    pageIndex: number
    top: number
    width: number
  }[]
  // オーディオブックマーク用のタイムスタンプ（秒）
  timestamp?: number
}

export type Highlight = {
  id: number
  episode_id: number
  media_type: MediaType
  position_data: HighlightPositionData
  text: string | null
  color: string
  note: string | null
  created_at: string
  updated_at: string
}

export type HighlightListResponse = {
  highlights: Highlight[]
  total: number
}

export type CreateHighlightRequest = {
  episode_id: number
  media_type: MediaType
  position_data: HighlightPositionData
  text?: string | null
  color?: string
  note?: string | null
}

export type UpdateHighlightRequest = {
  color?: string
  note?: string
}

/** 作品と話の情報つきのハイライト（本をまたいで一覧・引用するときに使う） */
export type HighlightSource = Highlight & {
  product_id: number
  product_title: string
  domain: string
  episode_title: string | null
  episode_index: number
}

export type HighlightSourceQuery = {
  /** カンマ区切りの id */
  ids?: string
  /** スペース区切りの AND で本文・メモ・作品名を検索 */
  q?: string
  /** 指定するとその作品だけを本文の順で返す */
  product_id?: number
  limit?: number
}

/** リーダーで開いたときにジャンプするハイライトの id を渡すクエリ名 */
export const HIGHLIGHT_JUMP_PARAM = 'hl'

// ============================================================
// サーバAPI（端末の記録を介さない。同期処理から使う）
// ============================================================

export const fetchEpisodeHighlightsFromServer = async (episodeId: number): Promise<Highlight[]> => {
  const res = await apiClient.get<HighlightListResponse>(`/highlights/episodes/${episodeId}`)
  return res.data.highlights
}

export const createHighlightOnServer = async (request: CreateHighlightRequest): Promise<Highlight> => {
  const res = await apiClient.post<Highlight>('/highlights', request)
  return res.data
}

export const updateHighlightOnServer = async (
  highlightId: number,
  request: UpdateHighlightRequest,
): Promise<Highlight> => {
  const res = await apiClient.patch<Highlight>(`/highlights/${highlightId}`, request)
  return res.data
}

export const deleteHighlightOnServer = async (highlightId: number): Promise<void> => {
  await apiClient.delete(`/highlights/${highlightId}`)
}

/** 作品をまたいでハイライトを取得する */
export const listHighlightSources = async (query: HighlightSourceQuery): Promise<HighlightSource[]> => {
  const res = await apiClient.get<{ highlights: HighlightSource[] }>('/highlights', { params: query })
  return res.data.highlights
}

// ============================================================
// ローカルファーストのハイライト操作
// 機内モードでも表示・追加・編集・削除できるよう端末（IndexedDB）にも持つ。
// 端末で作ったハイライトは負の仮 ID で返し、オンライン復帰時に useSyncEngine がサーバへ送る。
// ============================================================

const DEFAULT_HIGHLIGHT_COLOR = 'yellow'

/** 通信できずに失敗したか（HTTP のエラー応答は含まない） */
export const isNetworkError = (error: unknown): boolean => axios.isAxiosError(error) && !error.response

let lastTempId = 0
const nextTempId = (): number => {
  lastTempId = Math.min(lastTempId - 1, -Date.now())
  return lastTempId
}

const toHighlight = (h: OfflineHighlight): Highlight => ({
  id: h.serverId ?? -Number(h.localId.slice(1)),
  episode_id: h.episodeId,
  media_type: h.data.media_type,
  position_data: h.data.position_data,
  text: h.data.text ?? null,
  color: h.color,
  note: h.note,
  created_at: new Date(h.createdAt).toISOString(),
  updated_at: new Date(h.updatedAt).toISOString(),
})

const toOfflineHighlight = (h: Highlight, localId?: string): OfflineHighlight => ({
  localId: localId ?? `s${h.id}`,
  serverId: h.id,
  episodeId: h.episode_id,
  data: { episode_id: h.episode_id, media_type: h.media_type, position_data: h.position_data, text: h.text },
  note: h.note,
  color: h.color,
  createdAt: parseUtcDate(h.created_at).getTime(),
  updatedAt: parseUtcDate(h.updated_at).getTime(),
  deleted: 0,
  synced: 1,
})

/** 画面上の id（負なら端末で作った仮 ID）から端末の記録を探す */
const findLocalHighlight = async (highlightId: number): Promise<OfflineHighlight | undefined> => {
  try {
    if (highlightId < 0) return await getOfflineHighlight(`t${-highlightId}`)
    return await findOfflineHighlightByServerId(highlightId)
  } catch (error) {
    console.warn('[highlight] 端末の記録の読み込みに失敗:', error)
    return undefined
  }
}

/** サーバ操作が成功した後の端末への反映（失敗してもサーバ側の結果は返す） */
const syncLocalQuietly = async (task: () => Promise<void>) => {
  try {
    await task()
  } catch (error) {
    console.warn('[highlight] 端末の記録の更新に失敗:', error)
  }
}

/**
 * エピソードのハイライト一覧を取得
 *
 * オンラインならサーバの一覧を端末に保存し、端末で未送信の変更を重ねて返す。
 * オフライン時は端末の記録だけを返す。
 */
export const getEpisodeHighlights = async (episodeId: number): Promise<Highlight[]> => {
  let server: Highlight[] | null = null
  if (navigator.onLine) {
    try {
      server = await fetchEpisodeHighlightsFromServer(episodeId)
    } catch (error) {
      if (!isNetworkError(error)) throw error
    }
  }

  try {
    if (server)
      await replaceCachedHighlights(
        episodeId,
        server.map((h) => toOfflineHighlight(h)),
      )
    const local = await getOfflineHighlightsByEpisode(episodeId)
    return local.sort((a, b) => a.createdAt - b.createdAt).map(toHighlight)
  } catch (error) {
    if (!server) throw error
    console.warn('[highlight] 端末の記録の更新に失敗:', error)
    return server
  }
}

/**
 * ハイライトを作成（オフライン時は端末に保存して仮 ID で返す）
 */
export const createHighlight = async (request: CreateHighlightRequest): Promise<Highlight> => {
  if (navigator.onLine) {
    try {
      const created = await createHighlightOnServer(request)
      await syncLocalQuietly(() => saveOfflineHighlight(toOfflineHighlight(created)))
      return created
    } catch (error) {
      if (!isNetworkError(error)) throw error
    }
  }

  const now = Date.now()
  const record: OfflineHighlight = {
    localId: `t${-nextTempId()}`,
    serverId: null,
    episodeId: request.episode_id,
    data: request,
    note: request.note ?? null,
    color: request.color ?? DEFAULT_HIGHLIGHT_COLOR,
    createdAt: now,
    updatedAt: now,
    deleted: 0,
    synced: 0,
  }
  await saveOfflineHighlight(record)
  return toHighlight(record)
}

/**
 * ハイライトを更新（色・メモのみ。オフライン時は端末の記録を更新する）
 */
export const updateHighlight = async (highlightId: number, request: UpdateHighlightRequest): Promise<Highlight> => {
  const local = await findLocalHighlight(highlightId)
  const serverId = local ? local.serverId : highlightId > 0 ? highlightId : null

  if (serverId !== null && navigator.onLine) {
    try {
      const updated = await updateHighlightOnServer(serverId, request)
      await syncLocalQuietly(() => saveOfflineHighlight(toOfflineHighlight(updated, local?.localId)))
      return updated
    } catch (error) {
      if (!isNetworkError(error)) throw error
    }
  }

  if (!local) throw new Error('オフラインのため、このハイライトは更新できません')
  const record: OfflineHighlight = {
    ...local,
    color: request.color ?? local.color,
    note: request.note ?? local.note,
    updatedAt: Date.now(),
    synced: 0,
  }
  await saveOfflineHighlight(record)
  return toHighlight(record)
}

/**
 * ハイライトを削除（オフライン時は削除済みの印を付け、オンライン復帰時にサーバから消す）
 */
export const deleteHighlight = async (highlightId: number): Promise<void> => {
  const local = await findLocalHighlight(highlightId)
  // サーバに送る前のハイライトは端末から消すだけでよい
  if (local && local.serverId === null) {
    await deleteOfflineHighlight(local.localId)
    return
  }
  const serverId = local?.serverId ?? (highlightId > 0 ? highlightId : null)
  if (serverId === null) return

  if (navigator.onLine) {
    try {
      await deleteHighlightOnServer(serverId)
      if (local) await syncLocalQuietly(() => deleteOfflineHighlight(local.localId))
      return
    } catch (error) {
      if (!isNetworkError(error)) throw error
    }
  }

  if (!local) throw new Error('オフラインのため、このハイライトは削除できません')
  await saveOfflineHighlight({ ...local, deleted: 1, updatedAt: Date.now(), synced: 0 })
}

/**
 * リーダーを開いたときにジャンプするハイライトの位置を返す。
 * 端末の記録 → サーバの順に探し、別の話のハイライトや取得できない場合は null（進捗から開く）
 */
export const fetchHighlightJumpPosition = async (
  highlightId: number | null,
  episodeId: number,
): Promise<HighlightPositionData | null> => {
  if (highlightId === null) return null
  const local = await findLocalHighlight(highlightId)
  if (local) return local.episodeId === episodeId && !local.deleted ? local.data.position_data : null
  try {
    const [found] = await listHighlightSources({ ids: String(highlightId), limit: 1 })
    return found?.episode_id === episodeId ? found.position_data : null
  } catch (error) {
    console.warn('[highlight] ジャンプ先のハイライトを取得できませんでした:', error)
    return null
  }
}
